// Pure layout for the segmentation -> reconstruction lineage map. Kept free of
// React/DOM so the column/slot/band assembly (and its planarity) is unit-testable.
//
// The page list is already in global leaf order (pageIndex asc, then
// sectionBounds.order asc) and contains ONLY leaves — single-section pages and
// segment children; segmented PARENT pages are excluded. So:
//   - each leaf is one COLUMN (left to right),
//   - an ORIGINAL page is a SLOT: a run of consecutive leaves sharing a non-null
//     parentPageId (a segmented page), or a single parentPageId===null leaf,
//   - a reconstructed doc is a contiguous BAND spanning the columns of its
//     ordered pageIds (reconstruction groups consecutive leaves, so the span
//     never crosses another — the whole map is planar).

export type LineageLeaf = {
  id: string;
  parentPageId: string | null;
  heading?: string | null;
  classification?: string | null;
};

export type LineageEntity = {
  reconstructedDocumentId: string;
  classification: string;
  suggestedFilename?: string;
  pageIds: string[];
};

export type LeafCell = {
  id: string;
  col: number;
  isSegment: boolean;
  heading?: string | null;
  classification?: string | null;
  /** DR-2427: leaves represented by this cell. 1 for a per-leaf cell; >1 for a bucket. */
  count?: number;
};

export type PageSlot = {
  pageNo: number;
  firstCol: number;
  lastCol: number;
  segmented: boolean;
  parentPageId: string | null;
  childCols: number[];
  /** DR-2427: page id to open on click — the parent page for a segmented slot, else the leaf.
   *  Precomputed so the renderer never indexes the raw `pages` array by column. */
  openPageId: string;
  /** DR-2427: bucket page-range label (e.g. "12–47"); undefined for a per-page slot. */
  label?: string;
  /** DR-2427: original pages represented by a bucket slot; undefined for a per-page slot. */
  pageCount?: number;
};

export type EntityBand = {
  reconstructedDocumentId: string;
  classification: string;
  suggestedFilename?: string;
  firstCol: number;
  lastCol: number;
  cols: number[];
  /** DR-2427: distinct reconstructed docs aggregated into a bucket band; undefined per-page. */
  count?: number;
};

export type Lineage = {
  cells: LeafCell[];
  slots: PageSlot[];
  bands: EntityBand[];
  columnCount: number;
  /** DR-2427: true when adjacent leaves were aggregated into fixed-width buckets. */
  bucketed: boolean;
  /** DR-2427: leaves per rendered column (1 when not bucketed). */
  bucketSize: number;
};

/**
 * DR-2427: above this many leaf columns the map aggregates adjacent leaves into fixed-width
 * buckets so an 8k-page document doesn't render ~60–70k SVG nodes. Chosen below the visual
 * threshold the raw column count would allow: the renderer emits up to four node groups per
 * column (connector + 3 tiers), so capping columns here keeps total SVG nodes under ~5k
 * regardless of page count. At or below this count rendering is byte-identical to per-leaf.
 */
export const LINEAGE_MAX_COLUMNS = 1200;

export function buildLineage(
  leaves: LineageLeaf[],
  entities: LineageEntity[] = [],
  maxColumns: number = LINEAGE_MAX_COLUMNS,
): Lineage {
  const cells: LeafCell[] = leaves.map((l, col) => ({
    id: l.id,
    col,
    isSegment: l.parentPageId != null,
    heading: l.heading,
    classification: l.classification,
  }));

  const colById = new Map(cells.map((c) => [c.id, c.col]));

  // Original-page slots: group consecutive leaves by non-null parentPageId.
  const slots: PageSlot[] = [];
  for (const cell of cells) {
    const parentPageId = leaves[cell.col].parentPageId;
    const prev = slots[slots.length - 1];
    if (parentPageId != null && prev?.segmented && prev.parentPageId === parentPageId) {
      prev.lastCol = cell.col;
      prev.childCols.push(cell.col);
      continue;
    }
    slots.push({
      pageNo: slots.length + 1,
      firstCol: cell.col,
      lastCol: cell.col,
      segmented: parentPageId != null,
      parentPageId,
      childCols: [cell.col],
      openPageId: parentPageId ?? cell.id,
    });
  }

  // Reconstructed-doc bands: map each entity's ordered pageIds to columns. Drop
  // ids not in the current leaf set (stale entity rows); skip entities that map
  // to no column rather than drawing a zero-width band.
  const bands: EntityBand[] = [];
  for (const e of entities) {
    const cols = e.pageIds.map((id) => colById.get(id)).filter((c): c is number => c != null);
    if (cols.length === 0) continue;
    bands.push({
      reconstructedDocumentId: e.reconstructedDocumentId,
      classification: e.classification,
      suggestedFilename: e.suggestedFilename,
      firstCol: Math.min(...cols),
      lastCol: Math.max(...cols),
      cols,
    });
  }

  if (cells.length <= maxColumns) {
    return { cells, slots, bands, columnCount: cells.length, bucketed: false, bucketSize: 1 };
  }
  return bucketLineage(cells, slots, entities, maxColumns);
}

/** Most frequent key in a count map (first-seen wins ties); null when empty. */
function dominant(counts: Map<string, number>): string | null {
  let best: string | null = null;
  let bestN = -1;
  for (const [key, n] of counts) {
    if (n > bestN) {
      best = key;
      bestN = n;
    }
  }
  return best;
}

/**
 * DR-2427: collapse a per-leaf lineage into fixed-width buckets. Each bucket becomes one
 * column across all three tiers; adjacent bands of the same dominant classification merge
 * into one band. Aggregation rules (documented so the map's colours/status are predictable):
 *   - a bucket's classification / segmentation is the DOMINANT value among its leaves,
 *   - a bucket's page-range label spans the original page numbers it covers,
 *   - a band's classification is the dominant reconstructed-doc classification in its range,
 *     and its representative id (used for navigation) is the first entity seen there.
 */
function bucketLineage(
  cells0: LeafCell[],
  slots0: PageSlot[],
  entities: LineageEntity[],
  maxColumns: number,
): Lineage {
  const n = cells0.length;
  const bucketSize = Math.ceil(n / maxColumns);
  const bucketCount = Math.ceil(n / bucketSize);
  const bucketOf = (leafCol: number) => Math.floor(leafCol / bucketSize);

  // Original page number per leaf column (from the per-page slots).
  const pageNoByLeafCol: number[] = new Array<number>(n).fill(0);
  for (const s of slots0) for (const c of s.childCols) pageNoByLeafCol[c] = s.pageNo;

  type Agg = { firstLeafCol: number; lastLeafCol: number; total: number; segCount: number; classCounts: Map<string, number> };
  const aggs: Agg[] = Array.from({ length: bucketCount }, (_, b) => ({
    firstLeafCol: b * bucketSize,
    lastLeafCol: Math.min((b + 1) * bucketSize - 1, n - 1),
    total: 0,
    segCount: 0,
    classCounts: new Map<string, number>(),
  }));
  for (let c = 0; c < n; c++) {
    const agg = aggs[bucketOf(c)];
    agg.total += 1;
    if (cells0[c].isSegment) agg.segCount += 1;
    const cls = cells0[c].classification;
    if (cls) agg.classCounts.set(cls, (agg.classCounts.get(cls) ?? 0) + 1);
  }

  const cells: LeafCell[] = aggs.map((agg, b) => ({
    id: cells0[agg.firstLeafCol].id,
    col: b,
    isSegment: agg.segCount * 2 > agg.total,
    heading: null,
    classification: dominant(agg.classCounts),
    count: agg.total,
  }));

  const slots: PageSlot[] = aggs.map((agg, b) => {
    const firstPageNo = pageNoByLeafCol[agg.firstLeafCol];
    const lastPageNo = pageNoByLeafCol[agg.lastLeafCol];
    return {
      pageNo: firstPageNo,
      firstCol: b,
      lastCol: b,
      segmented: agg.segCount * 2 > agg.total,
      parentPageId: null,
      childCols: [b],
      openPageId: cells0[agg.firstLeafCol].id,
      label: firstPageNo === lastPageNo ? `${firstPageNo}` : `${firstPageNo}–${lastPageNo}`,
      pageCount: lastPageNo - firstPageNo + 1,
    };
  });

  // Reconstructed-doc coverage per bucket: which entities touch it + their dominant class.
  const colByLeafId = new Map(cells0.map((c) => [c.id, c.col]));
  type BandAgg = { classCounts: Map<string, number>; entityIds: Set<string>; firstEntityId: string };
  const bandAggs: (BandAgg | null)[] = new Array<BandAgg | null>(bucketCount).fill(null);
  for (const e of entities) {
    const buckets = new Set<number>();
    for (const id of e.pageIds) {
      const lc = colByLeafId.get(id);
      if (lc != null) buckets.add(bucketOf(lc));
    }
    for (const b of buckets) {
      const agg = bandAggs[b] ?? { classCounts: new Map<string, number>(), entityIds: new Set<string>(), firstEntityId: e.reconstructedDocumentId };
      agg.classCounts.set(e.classification, (agg.classCounts.get(e.classification) ?? 0) + 1);
      agg.entityIds.add(e.reconstructedDocumentId);
      bandAggs[b] = agg;
    }
  }

  // Merge adjacent buckets of the same dominant classification into a single band.
  const bands: EntityBand[] = [];
  let b = 0;
  while (b < bucketCount) {
    const agg = bandAggs[b];
    if (!agg) {
      b += 1;
      continue;
    }
    const cls = dominant(agg.classCounts) ?? '';
    const entityIds = new Set(agg.entityIds);
    let end = b;
    while (end + 1 < bucketCount) {
      const next = bandAggs[end + 1];
      if (!next || (dominant(next.classCounts) ?? '') !== cls) break;
      end += 1;
      next.entityIds.forEach((id) => entityIds.add(id));
    }
    const cols = Array.from({ length: end - b + 1 }, (_, i) => b + i);
    bands.push({
      reconstructedDocumentId: agg.firstEntityId,
      classification: cls,
      firstCol: b,
      lastCol: end,
      cols,
      count: entityIds.size,
    });
    b = end + 1;
  }

  return { cells, slots, bands, columnCount: bucketCount, bucketed: true, bucketSize };
}

// Lineage of one node: the columns + slot + band it touches, for hover highlight.
export function lineageOf(
  lin: Lineage,
  ref: { kind: 'leaf'; col: number } | { kind: 'slot'; pageNo: number } | { kind: 'band'; id: string },
): { cols: Set<number>; slotPageNo?: number; bandId?: string } {
  const cols = new Set<number>();
  if (ref.kind === 'leaf') cols.add(ref.col);
  if (ref.kind === 'slot') {
    const s = lin.slots.find((x) => x.pageNo === ref.pageNo);
    s?.childCols.forEach((c) => cols.add(c));
  }
  if (ref.kind === 'band') {
    const b = lin.bands.find((x) => x.reconstructedDocumentId === ref.id);
    b?.cols.forEach((c) => cols.add(c));
  }
  const slot = lin.slots.find((s) => [...cols].some((c) => c >= s.firstCol && c <= s.lastCol));
  const band = lin.bands.find((b) => [...cols].some((c) => b.cols.includes(c)));
  return { cols, slotPageNo: slot?.pageNo, bandId: band?.reconstructedDocumentId };
}
